// ProgramEntrance — branded "loading" cover that plays the *first* time
// the student enters a given program workspace inside a session. Same
// brand idiom as BrandEntrance on /student, but tinted forest (the
// workspace's primary surface) and fronted by a thin amber progress
// bar that sweeps from 0% → 33%. The 0–33% sweep is deliberate: it
// visualizes "the workspace is loading, you're a third of the way
// there" while the page fetches its data behind the cover, so the
// transition feels paced rather than abrupt.
//
// Replay rules — driven by sessionStorage so they survive route
// remounts inside the same workspace:
//   • Home (/student) → program           → plays
//   • Program → team page → back to ws    → does NOT play (still in program)
//   • Program → home → same program       → plays again (home cleared the flag)
//   • Program → home → different program  → plays (different flag)
//
// `clearProgramEntranceFlags()` is exported so the home and discover
// pages can wipe the flag set when the student leaves a program. There
// is intentionally no exit animation; the cover just fades on completion.

import { useEffect, useRef, useState } from "react";
import { Animated, Easing, Platform, Text, View } from "react-native";

import { COLORS, FONTS } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;
import Svg, { Circle, Rect } from "react-native-svg";


const SESSION_KEY_PREFIX = "coravu.programEntrance.";

/** Wipe every per-program "already shown the entrance this session"
 *  flag. Call from /student (home) and /student/find on focus so the
 *  next program-click replays the animation. */
export function clearProgramEntranceFlags() {
  if (Platform.OS !== "web") return;
  try {
    if (typeof window === "undefined") return;
    const ss = window.sessionStorage;
    if (!ss) return;
    const drop: string[] = [];
    for (let i = 0; i < ss.length; i++) {
      const k = ss.key(i);
      if (k && k.startsWith(SESSION_KEY_PREFIX)) drop.push(k);
    }
    for (const k of drop) ss.removeItem(k);
  } catch {
    // sessionStorage unavailable — fine; the next program mount will
    // simply replay the animation either way.
  }
}

export function ProgramEntrance({
  competitionId,
  programName,
  companyName,
}: {
  /** Used as the per-program session key — one entrance per program
   *  per session, replays after the student visits home/discover. */
  competitionId?: string | null | undefined;
  /** Optional — shown beneath the wordmark while the cover is up. */
  programName?: string | null;
  /** Optional — small kicker above the program name. */
  companyName?: string | null;
}) {
  const sessionKey = competitionId ? SESSION_KEY_PREFIX + competitionId : null;

  // Has the entrance for this specific program already played this
  // session? Read once at mount; subsequent remounts (e.g. coming
  // back from the team page within the same workspace) see the flag
  // and skip the animation entirely.
  const alreadyPlayed = (() => {
    if (Platform.OS !== "web" || !sessionKey) return false;
    try {
      return (
        typeof window !== "undefined" &&
        window.sessionStorage?.getItem(sessionKey) === "1"
      );
    } catch {
      return false;
    }
  })();

  // Mark fade-in / scale and the cover fade-out keep using `Animated`
  // (native driver) — those play smoothly on web. The progress bar's
  // width fill, however, has to use `useNativeDriver: false`, and on
  // RN-Web the JS-driven width interpolation reliably stutters / snaps
  // (especially when sequenced after a native-driver block). We drive
  // it with React state + a CSS transition instead, which is butter-
  // smooth on web and avoids the cross-driver Animated.sequence bug.
  const opacity = useRef(new Animated.Value(alreadyPlayed ? 0 : 1)).current;
  const markScale = useRef(new Animated.Value(alreadyPlayed ? 1 : 0.92)).current;
  const markOpacity = useRef(new Animated.Value(alreadyPlayed ? 1 : 0)).current;
  const [mounted, setMounted] = useState(!alreadyPlayed);
  const [progressTarget, setProgressTarget] = useState(alreadyPlayed ? 0.33 : 0);

  useEffect(() => {
    if (alreadyPlayed) return;
    // Sequence (overlapped to keep the bar moving for the whole hold):
    //   t=0     mark + wordmark fade-in starts (Animated, native)
    //   t=120   progress bar starts crawling 0% → 33% (CSS transition)
    //   t=480   mark fully visible
    //   t=1100  progress bar reaches 33% (920ms transition)
    //   t=1280  cover starts fading out
    //   t=1800  cover unmounts
    Animated.parallel([
      Animated.timing(markOpacity, {
        toValue: 1,
        duration: 280,
        useNativeDriver: true,
      }),
      Animated.timing(markScale, {
        toValue: 1,
        duration: 480,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();

    // Kick off the progress fill almost immediately — even before the
    // mark has finished fading in. Without this, the user spends ~280ms
    // staring at a 0% bar before anything moves, which reads as broken.
    const startProgress = setTimeout(() => setProgressTarget(0.33), 120);

    // Cover fade-out runs after the bar has had time to reach 33% and
    // hold for a beat (transition is 920ms; we wait an extra ~180ms).
    const fadeOut = setTimeout(() => {
      Animated.timing(opacity, {
        toValue: 0,
        duration: 520,
        easing: Easing.in(Easing.quad),
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (!finished) return;
        setMounted(false);
        // Record that we've already played the entrance for this
        // program in this session, so re-mounts of the workspace page
        // (e.g. coming back from the team page) skip the animation.
        // Cleared by clearProgramEntranceFlags() on home / discover.
        try {
          if (sessionKey && typeof window !== "undefined") {
            window.sessionStorage?.setItem(sessionKey, "1");
          }
        } catch {
          // sessionStorage unavailable — animation will just play on the
          // next mount as well.
        }
      });
    }, 1280);

    return () => {
      clearTimeout(startProgress);
      clearTimeout(fadeOut);
    };
  }, [alreadyPlayed, markOpacity, markScale, opacity, sessionKey]);

  if (!mounted) return null;

  const progressWidth = `${Math.round(progressTarget * 100)}%`;

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        Platform.OS === "web"
          ? ({
              position: "fixed" as unknown as "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: COLORS.FOREST.hex,
              alignItems: "center",
              justifyContent: "center",
              zIndex: 99999,
            } as object)
          : {
              position: "absolute" as const,
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: COLORS.FOREST.hex,
              alignItems: "center",
              justifyContent: "center",
              zIndex: 99999,
            },
        { opacity },
      ]}
    >
      {/* Soft amber radial — same depth cue the workspace hero uses. */}
      {Platform.OS === "web" ? (
        <View
          pointerEvents="none"
          style={[
            {
              position: "absolute",
              top: "32%" as unknown as number,
              left: "50%" as unknown as number,
              width: 540,
              height: 540,
              marginLeft: -270,
              marginTop: -200,
              borderRadius: 270,
            },
            {
              backgroundImage:
                `radial-gradient(circle, ${STUDENT_THEME.accentHalo} 0%, transparent 60%)`,
            } as object,
          ]}
        />
      ) : null}

      <Animated.View
        style={{
          alignItems: "center",
          opacity: markOpacity,
          transform: [{ scale: markScale }],
        }}
      >
        {/* Coravu targeting mark — same shape used elsewhere. */}
        <View
          style={{
            width: 64,
            height: 64,
            borderRadius: 14,
            backgroundColor: COLORS.FOREST_MID.hex,
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 18,
            shadowColor: "#000",
            shadowOpacity: 0.32,
            shadowRadius: 16,
            shadowOffset: { width: 0, height: 6 },
            borderWidth: 1,
            borderColor: STUDENT_THEME.sidebarItemActiveBg,
          }}
        >
          <Svg width={40} height={40} viewBox="0 0 34 34" fill="none">
            <Rect width={34} height={34} rx={7} fill={COLORS.FOREST_DEEP.hex} />
            <Circle cx={17} cy={17} r={11} stroke={COLORS.SAGE.hex} strokeWidth={0.7} />
            <Circle cx={17} cy={17} r={6.5} stroke={COLORS.AMBER.hex} strokeWidth={1.4} />
            <Circle cx={17} cy={17} r={2} fill={COLORS.AMBER.hex} />
          </Svg>
        </View>

        <Text
          style={{
            fontFamily: FONTS.displayMedium,
            fontSize: 36,
            letterSpacing: 5,
            color: COLORS.LINEN.hex,
          }}
        >
          COR<Text style={{ color: COLORS.AMBER.hex }}>A</Text>VU
        </Text>

        {companyName ? (
          <Text
            numberOfLines={1}
            style={{
              marginTop: 16,
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2.2,
              color: COLORS.AMBER.hex,
              textTransform: "uppercase",
            }}
          >
            {companyName}
          </Text>
        ) : null}
        {programName ? (
          <Text
            numberOfLines={2}
            style={{
              marginTop: companyName ? 6 : 16,
              fontFamily: FONTS.displayMedium,
              fontSize: 18,
              lineHeight: 22,
              color: "rgba(247,244,238,0.78)",
              textAlign: "center",
              maxWidth: 360,
              paddingHorizontal: 24,
            }}
          >
            {programName}
          </Text>
        ) : null}

        {/* Progress bar — fills 0 → 33% over the entrance. The track
            is a low-contrast linen tint and the fill is amber, so the
            bar reads as a subtle progress indicator rather than a hard
            brand stamp. */}
        <View
          style={{
            marginTop: 28,
            width: 240,
            height: 3,
            borderRadius: 2,
            backgroundColor: STUDENT_THEME.surfaceDividerFaint,
            overflow: "hidden",
          }}
        >
          {/* CSS-driven width fill — the cubic-bezier mirrors the
              Easing.out(quad) we'd otherwise use. Setting `width: '0%'`
              on first render and bumping to '33%' via state on a
              setTimeout gives a visibly smooth animation on web. */}
          <View
            style={[
              {
                height: 3,
                backgroundColor: COLORS.AMBER.hex,
                width: progressWidth as unknown as number,
                borderRadius: 2,
              },
              Platform.OS === "web"
                ? ({
                    transition: "width 920ms cubic-bezier(0.16, 1, 0.3, 1)",
                  } as object)
                : null,
            ]}
          />
        </View>
        <Text
          style={{
            marginTop: 12,
            fontFamily: FONTS.bodyBold,
            fontSize: 9,
            letterSpacing: 2.4,
            color: STUDENT_THEME.textOnSidebarIdle,
            textTransform: "uppercase",
          }}
        >
          Loading workspace
        </Text>
      </Animated.View>
    </Animated.View>
  );
}
